<template>
  <AppResizableContainer
    :layout="isHorizontalLayout ? 'horizontal' : 'vertical'"
    :min-size="150"
    class="overflow-auto border">
    <template #headerContent>
      <div class="h-full grid grid-rows-[1fr_auto] overflow-auto pb-4">
        <BaseTable
          table-id="salesDoc-charges-table"
          :headers="tableHeaders"
          :list="chargesTableLines"
          is-saving-layout-hidden
          is-search-hidden
          is-refresh-button-hidden
          is-download-button-hidden
          hide-grouping
          :is-add-new-hidden="mode === 'view'"
          @add-new="addNewChargeTable"
          :selected-row-id="newChargeLine.id"
          class="h-fit">
          <template #custom="{ item, header, index }: any">
            <!-- Expenses and Charges dropdown -->
            <div
              v-if="header == 'expensesAndCharges.description' && dropDownExpensesAndCharges.length"
              class="px-2">
              <BaseSelectDropdown
                name="expensesAndCharges"
                flat
                size="sm"
                :mode="mode"
                :items="dropDownExpensesAndCharges"
                :selected-item="getSelectedExpensesAndCharges(item)"
                @update:selected="handleExpensesAndChargesSelection($event, item)"
                :error="getValidationErrorCharges(index, 'expensesAndChargesId')" />
            </div>
            <!-- Charge Value type dropdown -->
            <div v-if="header == 'chargeValuetype'" class="px-2">
              <BaseSelectDropdown
                name="chargeValuetype"
                flat
                size="sm"
                mode="view"
                :items="dropdownChargeValueType"
                :selected-item="getSelectedChargeValueType(item)" />
            </div>

            <!-- Vat Category  -->
            <div v-if="header == 'vatCategory.name'" class="px-4 text-right">
              {{ getVatCategoryName(item) || 0 }}%
            </div>

            <!-- Vat -->
            <div v-if="header == 'vat'" class="px-4 text-right">
              {{ item.vatValue }}
            </div>

            <!-- Value -->
            <div v-if="header == 'value'" class="px-4">
              <BaseInput
                v-model="item[header]"
                color="base"
                flat
                size="sm"
                price
                :disabled="mode === 'view'"
                :mode="item.expensesAndCharges.allowValueChange ? 'edit' : 'view'"
                show-error-as-tooltip
                :error="getValidationErrorCharges(index, 'value')"
                @update:model-value="item.hasChanges = true" />
            </div>

            <!-- Description Input -->
            <div v-if="header == 'extraDescription'" class="px-4">
              <BaseInput
                v-model="item[header]"
                color="base"
                flat
                size="sm"
                :disabled="mode === 'view'"
                @update:model-value="item.hasChanges = true" />
            </div>
            <div v-if="header == 'grossValue'" class="pl-4">
              {{ item.grossValue ?? 0 }}
            </div>
          </template>
          <template #actions="{ item }: any"
            ><div class="flex items-center justify-center gap-1">
              <button
                v-if="item.hasChanges && mode === 'edit'"
                class="p-1"
                @click="saveChargeLine(item)">
                <IconSave class="cursor-pointer size-4 hover:text-theme-600" />
              </button>
              <button class="p-1" v-if="mode === 'edit'">
                <IconTrashOutline
                  class="cursor-pointer size-4 hover:text-red-600"
                  @click="deleteLine(item)" />
              </button></div
          ></template>
        </BaseTable>
        <div
          class="flex items-center justify-between w-full px-4 py-2 mt-2 border-y bg-theme-50 border-theme-200 h-fit text-theme-700">
          <span>{{ $t('salesDocuments.tabs.totals') }}</span> <span>{{ chargesTotal }} €</span>
        </div>
      </div>
    </template>
    <template #detailsContent>
      <div class="h-full grid grid-rows-[1fr_auto] overflow-auto pb-4">
        <!-- Taxes Table -->
        <BaseTable
          table-id="salesDoc-taxes-table"
          :headers="tableHeaders.filter((x) => x.value != 'vatCategory.name' && x.value != 'vat')"
          :list="taxesTableLines"
          is-saving-layout-hidden
          is-search-hidden
          is-refresh-button-hidden
          is-download-button-hidden
          hide-grouping
          :is-add-new-hidden="mode === 'view'"
          @add-new="addNewTaxTable"
          class="h-fit">
          <template #custom="{ item, index, header }: any">
            <!-- Select Expenses and Charges -->
            <div
              v-if="header == 'expensesAndCharges.description' && dropDownExpensesAndCharges.length"
              class="px-2">
              <BaseSelectDropdown
                name="expensesAndCharges"
                flat
                size="sm"
                :mode="mode"
                :items="dropDownTaxes"
                :selected-item="getSelectedTaxes(item)"
                @update:selected="handleExpensesAndChargesSelection($event, item)"
                :error="getValidationErrorTaxes(index, 'expensesAndChargesId')" />
            </div>
            <!-- Charge Value type dropdown -->
            <div v-if="header == 'chargeValuetype'" class="px-2">
              <BaseSelectDropdown
                name="chargeValuetype"
                flat
                size="sm"
                mode="view"
                :items="dropdownChargeValueType"
                :selected-item="getSelectedChargeValueType(item)" />
            </div>

            <!-- Vat Category  -->
            <div v-if="header == 'vatCategory.name'" class="pl-4">
              {{ getVatCategoryName(item) || '-' }}<span v-if="item.vatCategoryId">%</span>
            </div>

            <!-- Vat -->
            <div v-if="header == 'vat'" class="px-4">
              {{ item.vatValue || '-' }}
            </div>

            <!-- Value -->
            <div v-if="header == 'value'" class="px-4">
              <BaseInput
                v-model="item[header]"
                color="base"
                flat
                size="sm"
                price
                :disabled="mode === 'view'"
                :mode="item.expensesAndCharges.allowValueChange ? 'edit' : 'view'"
                show-error-as-tooltip
                :error="getValidationErrorTaxes(index, 'value')"
                @update:model-value="item.hasChanges = true" />
            </div>

            <!-- Description Input -->
            <div v-if="header == 'extraDescription'" class="px-4">
              <BaseInput
                v-model="item[header]"
                color="base"
                flat
                size="sm"
                :disabled="mode === 'view'"
                @update:model-value="item.hasChanges = true" />
            </div>

            <!-- Calculated GrossValue -->
            <div v-if="header == 'grossValue'" class="pl-4 text-right">
              {{ item.grossValue ?? 0 }}
            </div>
          </template>

          <template #actions="{ item }: any"
            ><div class="flex items-center justify-center gap-1">
              <button class="p-1" @click="saveChargeLine(item)">
                <IconSave
                  v-if="item.hasChanges && mode === 'edit'"
                  class="cursor-pointer size-4 hover:text-theme-600" />
              </button>
              <button class="p-1" v-if="mode === 'edit'">
                <IconTrashOutline
                  class="cursor-pointer size-4 hover:text-red-600"
                  @click="deleteLine(item)" />
              </button></div
          ></template>
        </BaseTable>
        <div
          class="flex items-center justify-between w-full px-4 py-2 mt-2 border-y bg-theme-50 border-theme-200 h-fit text-theme-700">
          <span>{{ $t('salesDocuments.tabs.totals') }}</span> <span>{{ taxesTotal }} €</span>
        </div>
      </div>
    </template>
  </AppResizableContainer>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import { storeToRefs } from 'pinia';
import { useSalesDocumentsStore } from '@/stores/salesDocuments';
import { useLookupsStore } from '@/stores/lookups';
import { useToastStore } from '@/stores/toast';
import { enumToDropdownItems } from '@/utils/enums';
import { v4 as uuidv4 } from 'uuid';
import { useI18n } from 'vue-i18n';

import AppResizableContainer from '@/components/common/AppResizableContainer.vue';
import BaseTable from '@/components/common/BaseTable.vue';
import BaseInput from '@/components/common/BaseInput.vue';
import BaseSelectDropdown from '@/components/common/BaseSelectDropdown.vue';
import IconTrashOutline from '@/assets/icons/IconTrashOutline.vue';
import IconSave from '@/assets/icons/IconSave.vue';

import {
  type DocumentHeaderExpensesAndCharges,
  eChargesType,
  eChargeValueType,
} from '@/types/Documents';
import { RecordMode, type SelectDropdownItem } from '@/types/Base';
import useVuelidate from '@vuelidate/core';
import { helpers, required } from '@vuelidate/validators';
import { deepCopy } from '@/utils/data';
import { getExpenseVatFactorForTraderSite } from '@/services/api/prices';
import { roundValue } from '@/utils/prices';

const { t: $t } = useI18n({ useScope: 'global' });
const { modifiedSalesDocument } = storeToRefs(useSalesDocumentsStore());
const { getExpensesAndChargesLookup } = useLookupsStore();
const { dropDownExpensesAndCharges, expensesAndCharges, dropDownTaxes } =
  storeToRefs(useLookupsStore());
const { updateHeaderTotals } = useSalesDocumentsStore();
const dropdownChargeValueType = ref(enumToDropdownItems(eChargeValueType));
const chargesTotal = ref(0);
const taxesTotal = ref(0);
const modifiedLines = ref<DocumentHeaderExpensesAndCharges[]>([]);
onMounted(async () => {
  await getExpensesAndChargesLookup();
  if (modifiedSalesDocument.value?.documentHeader.documentHeaderExpensesAndCharges) {
    modifiedLines.value = deepCopy<DocumentHeaderExpensesAndCharges[]>(
      modifiedSalesDocument.value?.documentHeader.documentHeaderExpensesAndCharges
    );
  }
  chargesTotal.value = calculateChargesTotal();
  taxesTotal.value = calculateTaxesTotal();
});

defineProps<{
  isHorizontalLayout: boolean;
  mode: 'view' | 'edit';
}>();

const tableHeaders = ref([
  {
    value: 'expensesAndCharges.description',
    text: $t('salesDocuments.expensesAndCharges'),
    type: 'custom',
    active: true,
    hasInlineEdit: false,
  },
  {
    value: 'chargeValuetype',
    text: $t('salesDocuments.chargeValueType'),
    type: 'custom',
    active: true,
    hasInlineEdit: false,
  },
  {
    value: 'vatCategory.name',
    text: $t('salesDocuments.vatCategory'),
    type: 'custom',
    active: true,
    hasInlineEdit: true,
  },
  { value: 'vat', text: $t('salesDocuments.vat'), type: 'custom', active: true },
  { value: 'value', text: $t('salesDocuments.value'), type: 'custom', active: true },
  {
    value: 'extraDescription',
    text: $t('salesDocuments.description'),
    type: 'custom',
    active: true,
  },
  {
    value: 'grossValue',
    text: $t('salesDocuments.grossValue'),
    type: 'custom',
    active: true,
    hasInlineEdit: true,
  },
  { value: 'actions', text: '', active: true },
]);

// Charges Table

function initNewChargeLine() {
  return {
    id: uuidv4(),
    isActive: true,
    isFromManager: true,
    grossValue: 0,
    value: 0,
    vatFactorValue: 0,
    vatValue: 0,
    expensesAndChargesId: '',
    expensesAndCharges: {
      id: '',
      isActive: true,
      isFromManager: true,
      code: '',
      description: '',
      chargeType: 0,
      allowValueChange: true,
      computedOnPrimaryValue: true,
      value: 0,
      chargeValueType: 0,
    },
    recordMode: RecordMode.Added,
  };
}

const newChargeLine = ref<DocumentHeaderExpensesAndCharges>(initNewChargeLine());

function addNewChargeTable() {
  newChargeLine.value = initNewChargeLine();

  if (modifiedLines.value && modifiedLines.value) {
    modifiedLines.value = [newChargeLine.value, ...modifiedLines.value];
  }
}

const chargesTableLines = computed(() => {
  if (!modifiedLines.value) return [];

  const filteredCharges = modifiedLines.value.filter(
    (item) =>
      (item.expensesAndCharges.chargeType === eChargesType.Charge ||
        item.expensesAndCharges.chargeType === eChargesType.Expense) &&
      item.recordMode !== RecordMode.Deleted
  );
  return [...filteredCharges];
});

function getSelectedExpensesAndCharges(item: DocumentHeaderExpensesAndCharges) {
  if (item.expensesAndChargesId) {
    const expChgSelected = dropDownExpensesAndCharges.value.find(
      (x) => x.value == item.expensesAndChargesId
    );
    if (expChgSelected) return expChgSelected;
  }
  return null;
}

function calculateChargesTotal() {
  return chargesTableLines.value.reduce((sum, item) => {
    const grossTotal = +item.value + (item.vatValue ?? 0);
    return roundValue(sum + grossTotal);
  }, 0);
}

// Taxes Table

function initNewTaxLine() {
  return {
    id: uuidv4(),
    isActive: true,
    isFromManager: true,
    grossValue: 0,
    value: 0,
    expensesAndChargesId: '',
    expensesAndCharges: {
      id: '',
      isActive: true,
      isFromManager: true,
      code: '',
      description: '',
      chargeType: eChargesType.Tax,
      allowValueChange: true,
      computedOnPrimaryValue: true,
      value: 0,
      chargeValueType: 0,
    },
    recordMode: RecordMode.Added,
  };
}

const newTaxLine = ref<DocumentHeaderExpensesAndCharges>(initNewTaxLine());

const taxesTableLines = computed(() => {
  if (!modifiedLines.value) return [];
  const filteredTaxes = modifiedLines.value.filter(
    (item) =>
      item.expensesAndCharges.chargeType == eChargesType.Tax &&
      item.recordMode !== RecordMode.Deleted
  );
  return [...filteredTaxes];
});

function addNewTaxTable() {
  newTaxLine.value = initNewTaxLine();
  if (modifiedLines.value) {
    modifiedLines.value = [newTaxLine.value, ...modifiedLines.value];
  }
}

function getSelectedTaxes(item: DocumentHeaderExpensesAndCharges) {
  if (item.expensesAndCharges) {
    const taxSelected = dropDownTaxes.value.find((x) => x.value == item.expensesAndChargesId);
    if (taxSelected) return taxSelected;
  }
  return null;
}

function getSelectedChargeValueType(item: DocumentHeaderExpensesAndCharges) {
  if (item.expensesAndChargesId) {
    const selectedValue = dropdownChargeValueType.value.find(
      (x) => x.value == item.expensesAndCharges.chargeValueType
    );
    if (selectedValue) return selectedValue;
  }
  return null;
}

function calculateTaxesTotal() {
  return taxesTableLines.value.reduce((sum, item) => {
    return roundValue(sum + item.grossValue);
  }, 0);
}

// Common

async function handleExpensesAndChargesSelection(
  event: SelectDropdownItem,
  item: DocumentHeaderExpensesAndCharges
) {
  const newExpense = expensesAndCharges.value.find((x) => x.id == event.value);
  const existingExpense = modifiedLines.value?.find((x) => x.id == item.id);
  if (newExpense && existingExpense) {
    existingExpense.hasChanges = true;

    existingExpense.expensesAndCharges = newExpense;
    existingExpense.expensesAndChargesId = newExpense.id;
    existingExpense.value = newExpense.value;
    if (
      modifiedSalesDocument.value &&
      modifiedSalesDocument.value.documentHeader.billingAddressId &&
      newExpense.vatCategoryId
    )
      existingExpense.vatFactorValue = await getExpenseVatFactorForTraderSite(
        modifiedSalesDocument.value.documentHeader.billingAddressId,
        newExpense.vatCategoryId
      );
    else existingExpense.vatFactorValue = undefined;
  }
}

function getVatCategoryName(item: DocumentHeaderExpensesAndCharges) {
  if (item.expensesAndCharges.vatCategory) return item.expensesAndCharges.vatCategory.name;
  return '';
}

const toastStore = useToastStore();

function saveChargeLine(item: DocumentHeaderExpensesAndCharges) {
  $v.value.$touch();
  if ($v.value.$invalid) return;

  const charge = modifiedLines.value.find((c) => c.id === item.id);
  if (!charge) {
    return;
  }

  charge.hasChanges = false;

  if (charge.recordMode !== RecordMode.Added) {
    charge.recordMode = RecordMode.Modified;
  }

  if (charge.expensesAndCharges.chargeType === eChargesType.Tax) {
    updateHeaderTotals();
    const netBeforeCharges = modifiedSalesDocument.value?.documentHeader.totalNetValue ?? 0;
    charge.grossValue = netBeforeCharges * (charge.value / 100);
  } else {
    charge.vatValue = +(+charge.value * (+(charge?.vatFactorValue ?? 0) / 100)).toFixed(2);
    charge.grossValue = +(+charge.value + (charge.vatValue ?? 0)).toFixed(2);
  }
  if (charge.recordMode === RecordMode.Added) {
    const index = modifiedLines.value.findIndex((c) => c.id === charge.id);
    if (index > -1) {
      const [removedItem] = modifiedLines.value.splice(index, 1);
      modifiedLines.value.push(removedItem);
    }
  }
  chargesTotal.value = calculateChargesTotal();
  taxesTotal.value = calculateTaxesTotal();

  updateModifiedSalesDocument();
  toastStore.success({ text: `${$t('common.toast.success')}` });
}

function deleteLine(item: DocumentHeaderExpensesAndCharges) {
  if (!modifiedLines.value) return;

  const index = modifiedLines.value.findIndex((x) => x.id === item.id);
  if (index === -1) return;
  if (item.recordMode === RecordMode.Added) {
    modifiedLines.value.splice(index, 1);
  } else {
    modifiedLines.value[index].recordMode = RecordMode.Deleted;
  }

  updateModifiedSalesDocument();
  toastStore.success({ text: `${$t('common.toast.success')}` });
}

function updateModifiedSalesDocument() {
  if (modifiedSalesDocument.value) {
    if (!modifiedSalesDocument.value.documentHeader.documentHeaderExpensesAndCharges) {
      modifiedSalesDocument.value.documentHeader.documentHeaderExpensesAndCharges = [];
    }
    modifiedSalesDocument.value.documentHeader.documentHeaderExpensesAndCharges = deepCopy(
      modifiedLines.value
    );
  }
}

const validationModel = computed(() => ({
  charges: chargesTableLines.value || [],
  taxes: taxesTableLines.value || [],
}));

const rules = computed(() => ({
  charges: {
    $each: helpers.forEach({
      expensesAndChargesId: {
        required: helpers.withMessage('Category is required', required),
      },
      value: {
        required: helpers.withMessage('Value is required', required),
      },
    }),
  },
  taxes: {
    $each: helpers.forEach({
      expensesAndChargesId: {
        required: helpers.withMessage('Category is required', required),
      },
      value: {
        required: helpers.withMessage('Value is required', required),
      },
    }),
  },
}));

function getValidationErrorCharges(index: number, header: string) {
  if (!$v.value.charges.$each.$response) return;
  const validationInput = $v.value.charges.$each.$response.$data[index]?.[header];
  if (!validationInput) return '';
  if (validationInput.$invalid && $v.value.charges.$each.$response.$errors[index])
    return $v.value.charges.$each.$response.$errors[index]?.[header][0].$message;
  else return '';
}
function getValidationErrorTaxes(index: number, header: string) {
  if (!$v.value.taxes.$each.$response) return;
  const validationInput = $v.value.taxes.$each.$response.$data[index]?.[header];
  if (!validationInput) return '';
  if (validationInput.$invalid && $v.value.taxes.$each.$response.$errors[index])
    return $v.value.taxes.$each.$response.$errors[index]?.[header][0].$message;
  else return '';
}
const $v = useVuelidate(rules, validationModel);
</script>
